/**
 * Email Service với PHP Backend
 * Gửi email qua PHP API trên subdomain WordPress
 * Thay thế EmailJS (đã hết quota)
 */

export interface LessonEmailData {
  studentName: string
  studentEmail?: string
  lessonName: string
  lessonId: string
  score: string // Ví dụ: "8/10"
  accuracy: string // Ví dụ: "80"
  duration: string // Thời gian học (phút)
  completedAt: string // Thời gian hoàn thành
  correctAnswers?: number
  totalQuestions?: number
}

export interface EmailResponse {
  success: boolean
  message: string
  results?: {
    user?: {
      success: boolean
      message: string
      recipient?: string
    }
    admin?: {
      success: boolean
      message: string
      recipient?: string
    }
  }
  error?: string
  timestamp?: string
}

class EmailServicePHP {
  // API endpoint trên subdomain WordPress
  private apiEndpoint = 'https://ebook.vatli365.vn/api/send-email.php'
  
  /**
   * Gửi email thông báo hoàn thành bài học
   */
  async sendLessonCompletionEmail(data: LessonEmailData): Promise<EmailResponse> {
    try {
      console.log('📧 Sending lesson completion email via PHP API...')
      console.log('📧 Data:', data)
      
      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      })
      
      const result = await response.json()
      
      if (response.ok && result.success) {
        console.log('✅ Email sent successfully:', result)
        return result
      } else {
        console.error('❌ Email sending failed:', result)
        return {
          success: false,
          message: result.message || 'Failed to send email',
          error: result.error
        }
      }
      
    } catch (error: any) {
      console.error('❌ Email service error:', error)
      return {
        success: false,
        message: 'Network error or server unavailable',
        error: error.message
      }
    }
  }
  
  /**
   * Gửi email cho user và admin khi hoàn thành bài học
   */
  async notifyLessonCompletion(
    studentName: string,
    studentEmail: string | undefined,
    lessonName: string,
    lessonId: string,
    correctAnswers: number,
    totalQuestions: number,
    durationSeconds: number
  ): Promise<EmailResponse> {
    
    // Tính toán các metrics
    const accuracy = totalQuestions > 0 
      ? Math.round((correctAnswers / totalQuestions) * 100) 
      : 0
    
    const durationMinutes = Math.round(durationSeconds / 60)
    
    const emailData: LessonEmailData = {
      studentName: studentName || 'Học sinh',
      studentEmail: studentEmail,
      lessonName,
      lessonId,
      score: `${correctAnswers}/${totalQuestions}`,
      accuracy: accuracy.toString(),
      duration: durationMinutes.toString(),
      completedAt: new Date().toLocaleString('vi-VN'),
      correctAnswers,
      totalQuestions
    }
    
    console.log('📧 Preparing to send completion notification:', emailData)
    
    return await this.sendLessonCompletionEmail(emailData)
  }
  
  /**
   * Gửi email thông báo hoàn thành phần lý thuyết
   */
  async notifyTheoryCompletion(
    studentName: string,
    studentEmail: string,
    lessonName: string,
    lessonId: string,
    stats: {
      totalQuestions: number
      completedQuestions: number
      timeSpent: number
      sectionsCompleted: number
      totalSections: number
      // ✅ Optional fields để đảm bảo backward compatibility
      totalAnswered?: number
      correctAnswers?: number
      wrongAnswers?: number
      accuracy?: number // ✅ Accuracy đúng logic: correctAnswers / totalAnswered
      progress?: number // ✅ Progress: totalAnswered / totalQuestions
    }
  ): Promise<EmailResponse> {
    try {
      console.log('📧 Preparing to send theory completion notification...')
      
      // ✅ FIX: Dùng accuracy từ caller nếu có, nếu không mới tính (backward compatibility)
      const accuracy = stats.accuracy !== undefined 
        ? stats.accuracy 
        : (stats.correctAnswers !== undefined && stats.totalAnswered !== undefined && stats.totalAnswered > 0
          ? Math.round((stats.correctAnswers / stats.totalAnswered) * 100)
          : 0)
      
      // ✅ FIX: Dùng progress từ caller nếu có
      const progressPercentage = stats.progress !== undefined
        ? stats.progress
        : Math.min(Math.round((stats.sectionsCompleted / stats.totalSections) * 100), 100)
      
      const durationMinutes = Math.round(stats.timeSpent / 60)
      
      // ✅ FIX: Score = số câu đúng / tổng câu (nếu có correctAnswers)
      const score = stats.correctAnswers !== undefined
        ? `${stats.correctAnswers}/${stats.totalQuestions}`
        : `${stats.completedQuestions}/${stats.totalQuestions}`
      
      const emailData: LessonEmailData = {
        studentName: studentName || 'Học sinh',
        studentEmail: studentEmail,
        lessonName,
        lessonId,
        score, // ✅ Score đúng: số câu đúng / tổng câu
        accuracy: accuracy.toString(),
        duration: durationMinutes.toString(),
        completedAt: new Date().toLocaleString('vi-VN'),
        correctAnswers: stats.correctAnswers !== undefined ? stats.correctAnswers : stats.completedQuestions, // ✅ Dùng correctAnswers nếu có
        totalQuestions: stats.totalQuestions
      }
      
      console.log('📧 Theory completion data:', {
        ...emailData,
        progressPercentage,
        sectionsCompleted: stats.sectionsCompleted,
        totalSections: stats.totalSections
      })
      
      return await this.sendLessonCompletionEmail(emailData)
    } catch (error) {
      console.error('❌ Error sending theory completion email:', error)
      return {
        success: false,
        message: `Failed to send theory completion email: ${error instanceof Error ? error.message : 'Unknown error'}`
      }
    }
  }

  /**
   * Format kết quả email để hiển thị cho user
   */
  formatEmailResultMessage(result: EmailResponse): string {
    if (result.success) {
      const messages: string[] = []
      
      if (result.results?.user?.success) {
        messages.push('✅ Email đã được gửi đến bạn')
      }
      
      if (result.results?.admin?.success) {
        messages.push('✅ Thông báo đã được gửi đến giáo viên')
      }
      
      return messages.join('\n') || '✅ Email đã được gửi thành công!'
    } else {
      return `❌ Không thể gửi email: ${result.message}`
    }
  }
}

// Export singleton instance
export const emailServicePHP = new EmailServicePHP()

export default EmailServicePHP

